import { ChevronDown } from "lucide-react";
import { usePageContext } from "vike-react/usePageContext";

import { useChat } from "@/contexts/ChatContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";

import { LAUNCHER_OPEN_LABEL, LAUNCHER_OPEN_UNREAD_LABEL } from "./chatCopy";

/**
 * The single floating launcher (UA-01, D-01, D-04, UA-03).
 *
 * TAKES OVER THE FLOATING WHATSAPP BUTTON'S SLOT rather than sitting beside it. That component is
 * deleted in this phase, so after it there is exactly ONE `fixed bottom-4 z-50` round element on any
 * page. The disc, the mascot, the optical nudge, the `/admin` early return and the `/merchant` side
 * flip are all inherited from it verbatim: the corner already reads as "ناصح lives here" and
 * changing the disc would throw that recognition away to no purpose. Only the element type, the
 * focus ring and the unread dot are new.
 *
 * IT EMITS NO POSTHOG CALL OF ITS OWN. `openPanel` / `closePanel` on the context own
 * `chat_panel_opened` / `chat_panel_closed`, per CLAUDE.md's shared-primitive rule: the panel can be
 * closed from the header and by `Escape` too, and a capture at each call site would be three
 * versions of one event.
 *
 * SSR: mounted inside `BrowserWidgets`' post-mount gate in `+Layout.tsx`, which is what makes the
 * whole shell hydration-safe. Do not move it out of that gate.
 */
export default function ChatLauncher() {
  const pageContext = usePageContext();
  const { open, openPanel, closePanel, unreadDot } = useChat();
  const isMobile = useIsMobile();
  const pathname = pageContext.urlPathname || "";

  if (pathname.startsWith("/admin") || pathname.startsWith("/moderator")) return null;
  // No floating chat button inside the moderator preview iframe (post-mount only, hydration-safe).
  if (isEmbeddedPreview()) return null;

  // The merchant portal's sidebar sits on the right (RTL) with its "العودة إلى يتفرّج" footer
  // link at the bottom-right corner — park the button bottom-left there so it never covers it.
  const side = pathname.startsWith("/merchant") ? "left-4" : "right-4";

  // Mobile, open: the launcher is not rendered at all. The full-screen sheet covers it and its own
  // header close control is the way out; two dismiss affordances 16px apart is a mis-tap generator.
  if (isMobile && open) return null;

  // `unreadDot` is a TRI-STATE and `null` means "not yet known": both of its inputs resolve
  // asynchronously (a fetch for the count, a guarded storage read for the last-seen mark). Rendering
  // the dot only on an explicit `true` is what stops it popping into the disc a beat after
  // hydration.
  const hasUnread = unreadDot === true && !open;

  // The dot sits at the disc's top corner FACING THE PAGE INTERIOR, so it mirrors with the side
  // flip. Never the outer corner, which overhangs the viewport gutter. In RTL, `inline-end` is the
  // left, which is the interior at the default right anchor.
  const dotCorner = side === "left-4" ? "top-0 start-0" : "top-0 end-0";

  // Desktop, open: the launcher becomes the collapse control. `hover:scale-110` is dropped, because
  // a control that is currently "on" should not bounce, and the background holds at the hover stop
  // so the disc reads as pressed.
  const discState = open
    ? "bg-[var(--ys-primary-hover)]"
    : "bg-[var(--ys-primary)] hover:scale-110 hover:bg-[var(--ys-primary-hover)]";

  const label = hasUnread ? LAUNCHER_OPEN_UNREAD_LABEL : LAUNCHER_OPEN_LABEL;

  return (
    <button
      type="button"
      // Read by `ChatPanel` to return focus here when `Escape` closes the desktop card. An attribute
      // rather than a shared ref, because the two components are siblings under `BrowserWidgets` and
      // a ref would mean lifting state purely to hand a DOM node across.
      data-chat-launcher="true"
      aria-label={label}
      title={label}
      onClick={() => (open ? closePanel() : openPanel())}
      className={`fixed bottom-4 ${side} z-50 flex h-14 w-14 items-center justify-center rounded-full ${discState} text-white shadow-lg shadow-black/20 transition-transform duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-orange-700)] focus-visible:ring-offset-2`}
    >
      {open ? (
        <ChevronDown size={24} strokeWidth={2} aria-hidden="true" />
      ) : (
        <img
          src="/brand/mascot.png"
          alt=""
          aria-hidden="true"
          className="h-10 w-10 object-contain"
          style={{
            marginLeft: 4,
            marginTop: -6,
          }}
          loading="lazy"
        />
      )}

      {/* A DOT, NEVER A COUNT (D-04). A number invites a badge-clearing compulsion the thread does
          not reward, and 12px of dot reads as a badge that wants one in it.

          `aria-hidden`, because the signal is carried in the button's accessible name above. There
          is deliberately NO live region on this dot: D-04 is a page-load check, and announcing it
          on every navigation would be noise.

          The white ring is load bearing, not decorative: over the orange disc a bare red circle is
          nearly invisible, and `--ys-danger` against `--ys-paper` measures 4.54:1. */}
      {hasUnread && (
        <span
          aria-hidden="true"
          className={`absolute ${dotCorner} h-2.5 w-2.5 rounded-full bg-[var(--ys-danger)] ring-2 ring-[var(--ys-paper)]`}
        />
      )}
    </button>
  );
}
